import React, { useState, useEffect } from "react";
import axios from "@/lib/axios.js";
import { useToast } from "@/hooks/use-toast.js";
import { Mail, Phone, User, Users, CheckCircle2 } from "lucide-react";
import SecretShopperSkeleton from "../SecretShopper/SecretShopperSkeleton.jsx";
import { Button } from "@/components/ui/button.jsx";
import { faker } from "@faker-js/faker";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog.jsx";
import InfoCard from "../SecretShopper/InfoCard.jsx";
import { TabsContent } from "@/components/ui/tabs.jsx";
import SecretShopperLogs from "../SecretShopper/SecretShopperLogs.jsx";
import MissedOpportunityAnalysis from "../SecretShopper/MissedOpportunityAnalysis.jsx";
import { Separator } from "@/components/ui/separator.jsx";

const SecretShopperTab = ({ community }) => {
    const [secretShoppers, setSecretShoppers] = useState([]);
    const [isRecordStepsModalOpen, setIsRecordStepsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
    const { toast } = useToast();

    const activeSecretShopper = secretShoppers.find(({ is_active }) => is_active);
    const hasGenerateProspect = !activeSecretShopper;
    const hasSecretShops = !!(secretShoppers?.length > 0)

    const { id: communityId, website_url: websiteUrl } = community;

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`/communities/${communityId}/secret-shoppers`);

            const sortedSecretShoppers = (response.data || [])
                ?.sort((a, b) => {
                    if (a.is_active === b.is_active) {
                        return a.id - b.id; // secondary sort by id ascending
                    }
                    return b.is_active - a.is_active; // true (1) comes before false (0)
                });

            setSecretShoppers(sortedSecretShoppers)
        } catch (error) {
            toast({ title: "Error fetching data", variant: "destructive" });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleLeadSubmitted = async () => {
        setIsLoading(true);

        if (!activeSecretShopper?.id) {
            return;
        }

        try {
            await axios.post(`/secret-shopper/${activeSecretShopper?.id}/initiate`);
            setIsRecordStepsModalOpen(false);
            fetchData()
        } catch (error) {
            console.error(error);
            toast({ title: "Error Initiating", variant: "destructive" });
            setIsLoading(false);
        }
    }

    const generateProspectData = () => {
        const firstName = faker.person.firstName();
        const lastName = faker.person.lastName();
        return {
            first_name: firstName,
            last_name: lastName,
            email: faker.internet.email({
                firstName,
                lastName,
                provider: 'fizmails.com',
                options: { allowSpecialCharacters: true }
            }).toLowerCase(),
            community_id: communityId
        };
    };

    const createSecretShopper = async () => {
        setIsLoading(true);
        try {
            const data = generateProspectData();
            const response = await axios.post("/secret-shopper", data);
            const sortedSecretShoppers = ([response.data, ...secretShoppers])
                ?.sort((a, b) => {
                    if (a.is_active === b.is_active) {
                        return a.id - b.id; // secondary sort by id ascending
                    }
                    return b.is_active - a.is_active; // true (1) comes before false (0)
                });
            setSecretShoppers(sortedSecretShoppers);
            toast({ title: 'Created secret shopper!' });
            setIsRecordStepsModalOpen(true)
        } catch (error) {
            toast({
                title: "Error",
                description: "Error creating secret shopper",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading) return <SecretShopperSkeleton />;

    return (
        <TabsContent value="secret-shopper" className="grid gap-5">
            <MissedOpportunityAnalysis community={community} secretShoppers={secretShoppers} />
            {community?.status === 'secret-shopper' && hasSecretShops && (
                <>
                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                        <div className="flex items-center justify-between gap-6">
                            <div className="flex-1">
                                <h3 className="text-sm font-semibold text-slate-900 mb-3">
                                    Enjoy secret shopping? See other ways we drive accountability &amp; visibility.
                                </h3>
                                <div className="space-y-2">
                                    <div className="flex items-start gap-2">
                                        <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                                        <span className="text-xs text-slate-700">Automatic calls within 60 seconds of lead submission</span>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                                        <span className="text-xs text-slate-700">Instant text follow-up for missed calls</span>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                                        <span className="text-xs text-slate-700">AI call scoring to improve agent performance</span>
                                    </div>
                                    <div className="flex items-start gap-2">
                                        <CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" />
                                        <span className="text-xs text-slate-700">Detailed analytics to track outcomes and improve response rates</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex-shrink-0 flex items-center">
                                <Dialog open={isDemoModalOpen} onOpenChange={setIsDemoModalOpen}>
                                    <DialogTitle>
                                    </DialogTitle>
                                    <DialogTrigger asChild>
                                        <button
                                            className="px-6 py-3 text-base font-medium text-white rounded-lg shadow-sm hover:shadow-md transition-all duration-200 hover:scale-105"
                                            style={{ backgroundColor: "#FF8A66" }}
                                        >
                                            Book a Demo
                                        </button>
                                    </DialogTrigger>
                                    <DialogContent
                                        className="max-w-5xl p-0 overflow-hidden"
                                        style={{ borderRadius: "12px" }}
                                    >
                                        <div className="p-6 bg-white">
                                            <iframe
                                                src="https://meetings.hubspot.com/eden33?embed=true"
                                                style={{
                                                    width: "100%",
                                                    height: "700px",
                                                    border: "none",
                                                    minWidth: "820px",
                                                    borderRadius: "8px",
                                                }}
                                                title="Schedule Demo"
                                                allowtransparency="true"
                                            />
                                        </div>
                                    </DialogContent>
                                </Dialog>
                            </div>
                        </div>
                    </div>
                    <Separator className="my-4" />
                </>
            )}
            <div className="grid grid-cols-2">
                <h2 className="text-gray-500 text-xl font-medium">Secret Shops</h2>
                {!!hasGenerateProspect &&
                    <div className="flex w-full justify-end">
                        <Button variant="secondary" onClick={createSecretShopper}>
                            Generate New Prospect
                        </Button>
                    </div>
                }
            </div>

            {secretShoppers?.map((shopper) => (<SecretShopperLogs key={shopper.id} secretShopper={shopper} community={community} initiateCallback={() => {setIsRecordStepsModalOpen(true)}}/>))}


            <Dialog open={isRecordStepsModalOpen} onOpenChange={setIsRecordStepsModalOpen}>
                <DialogContent>
                    <DialogHeader>
                        <p className="text-gray-700">
                            <i className="underline font-semibold">Follow these steps:</i>
                            <ol className="list-decimal list-inside mt-4 space-y-2 grid gap-2 mb-4">
                                <li>
                                    Copy the prospect details using the copy buttons.
                                </li>
                                <li>
                                    Submit / paste them via the ILS or website contact form.
                                    {!!websiteUrl && (
                                        <>
                                            <br />
                                            <a
                                                className="underline hover:text-blue-500"
                                                href={websiteUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                Here is a link to your website
                                            </a>
                                        </>
                                    )}
                                </li>
                                <li>
                                    When done, click "I submitted the lead".
                                </li>
                            </ol>

                            <div className="grid grid-cols-1 gap-4 mb-4">
                                <InfoCard
                                    hasCopyToClipboard
                                    icon={User}
                                    label="First Name"
                                    value={activeSecretShopper?.first_name}
                                />
                                <InfoCard
                                    hasCopyToClipboard
                                    icon={Users}
                                    label="Last Name"
                                    value={activeSecretShopper?.last_name}
                                />
                                <InfoCard
                                    hasCopyToClipboard
                                    icon={Mail}
                                    label="Email"
                                    value={activeSecretShopper?.email}
                                />
                                <InfoCard
                                    hasCopyToClipboard
                                    icon={Phone}
                                    label="Phone Number"
                                    value={activeSecretShopper?.phone_number?.replace(/^(\+1)/, '').replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')}
                                />
                            </div>
                        </p>
                    </DialogHeader>
                    <DialogFooter>
                        <Button onClick={handleLeadSubmitted}>
                            I submitted the lead
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

        </TabsContent>
    );
};

export default SecretShopperTab;
